<template>
  <b-card no-body>
    <b-row class="mx-1 mt-2 mb-2" align-h="between" align-v="center">
      <b-col cols="12" lg="4" class="d-flex align-items-start justify-content-start">
        <h4 class="mb-0">Threats</h4>
      </b-col>
      <b-col cols="12" lg="4" class="d-flex align-items-center justify-content-end">
        <div class="form-select">
          Show
          <b-form-select
            v-model="perPage"
            :options="perPageOptions"
            style="width: 60px;"
            size="sm"
          />
          Entries
        </div>
        <b-form-select
          class="ml-3"
          v-model="selectedType"
          :options="typeOptions"
          size="sm"
          style="max-width: 160px;"
        />
      </b-col>
      <b-col cols="12" lg="4" class="d-flex justify-content-end align-items-center">
        <SearchBar>
          <template #input>
            <b-form-input v-model="filter" type="search" placeholder="Search" />
          </template>
        </SearchBar>
      </b-col>
    </b-row>

    <div class="mx-1">
      <component
        :is="currentComponent"
        :timeRange="timeRange"
        :url="xtendAlertsListUrl"
        :filter="filter"
        :perPage="perPage"
        :items="selectedType === 'network' ? networkItems
          : selectedType === 'endpointAlerts' ? endpointAlertItems
          : selectedType === 'endpointThreats' ? endpointThreatItems
          : null"
        :navigator-config="navigatorConfig"
        :network-items="selectedType === 'all' ? networkItems : null"
        :endpoint-alert-items="selectedType === 'all' ? endpointAlertItems : null"
        :endpoint-threat-items="selectedType === 'all' ? endpointThreatItems : null"
       />
    </div>
  </b-card>
</template>

<script>
import SearchBar from '@/xvisor/components/SearchBar.vue';
import NetworkThreatTable from '@/xvisor/components/security/NetworkThreatTable.vue';
import EndpointAlertsTab from '@/xvisor/components/security/EndpointAlertsTab.vue';
import EndpointThreatsTable from '@/xvisor/components/security/EndpointThreatsTable.vue';
import ThreatsAllTable from '@/xvisor/components/security/ThreatsAllTable.vue';

export default {
  components: {
    SearchBar,
    NetworkThreatTable,
    EndpointAlertsTab,
    EndpointThreatsTable,
    ThreatsAllTable,
  },
  props: {
    timeRange: { type: Object, required: true },
  },
  data() {
    return {
      xtendAlertsListUrl: '/security/endpoint/alerts/list.json',
      NetworkThreatTableUrl: '/alerts/ip/threats.json',
      EndpointThreatsTableUrl: '/alerts/endpoint/threats.json',
      filter: '',
      selectedType: 'all',
      typeOptions: [
        { value: 'all', text: 'All' },
        { value: 'network', text: 'Network' },
        { value: 'endpointAlerts', text: 'Endpoint Deterministics' },
        { value: 'endpointThreats', text: 'Endpoint Threats' },
      ],
      perPage: 20,
      perPageOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
      ],
      cacheByRange: {},
      loadingByType: {
        network: false,
        endpointAlerts: false,
        endpointThreats: false,
      },
      errorByType: {
        network: null,
        endpointAlerts: null,
        endpointThreats: null,
      },
    };
  },
  computed: {
    currentComponent() {
      switch (this.selectedType) {
        case 'network': return 'NetworkThreatTable';
        case 'endpointAlerts': return 'EndpointAlertsTab';
        case 'endpointThreats': return 'EndpointThreatsTable';
        case 'all':
        default: return 'ThreatsAllTable';
      }
    },
    rangeKey() {
      const start = this.timeRange?.start?.toISOString?.() ?? '';
      const end = this.timeRange?.end?.toISOString?.() ?? '';
      return `${start}|${end}`;
    },

    cached() {
      return this.cacheByRange[this.rangeKey] || {};
    },

    networkItems() {
      return this.cached.network || null;
    },
    endpointAlertItems() {
      return this.cached.endpointAlerts || null;
    },
    endpointThreatItems() {
      return this.cached.endpointThreats || null;
    },
    navigatorConfig() {
      return {
        baseUrl: '/navigator/#layerURL=',
        networkLayerBase: '/alerts/ip/',
        endpointLayerBase: '/alerts/endpoint/',
      };
    },
  },
  watch: {
    selectedType() {
      this.filter = '';
      this.ensureLoadedForSelection();
    },

    timeRange() {
      this.ensureLoadedForSelection();
    },
  },
  mounted() {
    this.ensureLoadedForSelection();
  },
  methods: {
    ensureLoadedForSelection() {
      if (this.selectedType === 'all') {
        this.ensureLoaded('network');
        this.ensureLoaded('endpointAlerts');
        this.ensureLoaded('endpointThreats');
        return;
      }
      this.ensureLoaded(this.selectedType);
    },

    setCache(rangeKey, type, items) {
      const existing = this.cacheByRange[rangeKey] || {};
      this.$set(this.cacheByRange, rangeKey, { ...existing, [type]: items });
    },

    async ensureLoaded(type) {
      if (this.cached[type]) return;
      if (this.loadingByType[type]) return;

      const { rangeKey } = this;

      this.$set(this.loadingByType, type, true);
      this.$set(this.errorByType, type, null);

      try {
        const start = this.timeRange.start.toISOString();
        const end = this.timeRange.end.toISOString();

        if (type === 'network') {
          const resp = await this.$http.get(this.NetworkThreatTableUrl, { params: { start, end } });
          this.setCache(rangeKey, 'network', Array.isArray(resp.data) ? resp.data : []);
          return;
        }

        if (type === 'endpointAlerts') {
          const resp = await this.$http.get(this.xtendAlertsListUrl, { params: { start, end } });
          this.setCache(rangeKey, 'endpointAlerts', Array.isArray(resp.data) ? resp.data : []);
          return;
        }

        if (type === 'endpointThreats') {
          const resp = await this.$http.get(this.EndpointThreatsTableUrl, { params: { start, end } });
          this.setCache(rangeKey, 'endpointThreats', Array.isArray(resp.data) ? resp.data : []);
          return;
        }
      } catch (e) {
        this.$set(this.errorByType, type, e);
        this.setCache(rangeKey, type, []);
      } finally {
        this.$set(this.loadingByType, type, false);
      }
    },
  },
};
</script>
